import React, { useState } from 'react';
import { X, Warning } from '@phosphor-icons/react';
import { useAnalyzerReportsContext } from '@Context/AnalyzerReportsContext';
import { AnalyzerWeeklyPrompt } from '@Models/types';

interface DeletePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  prompt: AnalyzerWeeklyPrompt | null;
}

export const DeletePromptModal: React.FC<DeletePromptModalProps> = ({
  isOpen,
  onClose,
  prompt,
}) => {
  const [loading, setLoading] = useState(false);
  const { deletePrompt } = useAnalyzerReportsContext();

  const handleDelete = async () => {
    if (!prompt) return;

    setLoading(true);
    try {
      await deletePrompt(prompt.promptTitle, prompt.authorEmail);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !prompt) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Delete Prompt</h2>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <Warning size={24} className="text-red-500" weight="fill" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Are you sure?
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                You are about to delete the prompt "{prompt.promptTitle}". This
                action cannot be undone.
              </p>
              {prompt.isDefault && (
                <p className="mt-2 text-sm font-medium text-red-600">
                  Warning: This is the default prompt. Deleting it may affect
                  ongoing operations.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Deleting...' : 'Delete Prompt'}
          </button>
        </div>
      </div>
    </div>
  );
};
